/** @jsx h */
import { h, Component } from 'preact';
import { lazy, Suspense } from 'preact/compat';
import { Router } from 'preact-router';
import { Provider } from 'react-redux';
import AsyncRoute from 'preact-async-route';
import 'promise-polyfill/src/polyfill';
import { paymentRoutes } from '../alias/paymentRoutes';
import { activationRoutes } from '../alias/activationRoutes';
import {
  buyWithInstacredLandingRoute,
  instacredHomeRoutes,
} from '../alias/homeRoutes';
import { pageNotFoundRoute } from '../alias/commonRoutes';
import {
  widgetCommunicationRoute,
  widgetCheckEligibleRoute,
  widgetBenefitRoute,
} from '../alias/commonRoutes';
import NotFound from '../routes/page-not-found';
import Utils, { setLocalStorage, setSessionStorage } from '../utils';
import store from '../store';
import AppContainer from './appContainer';
import Spinner from './spinner/spinner';
import { commonRoutes } from '../alias/commonRoutes';
import { checkoutWidgetRoutes } from '../alias/checkoutWidgetRoutes'
import { PrivateRoute } from './privateRoute';
import { scrollTo } from '../utils/scrollHandler';
import { commonActionType, navBarConfigActionType } from '../actionTypes';
import {
  validateAndSetClientIdCookie,
  CLIENT_ID_COOKIE_KEY,
} from '../tracking/helper';
import Helmet from 'preact-helmet';
import identifyUserAction from './../actions/identifyUserAction/index';
import Redirect from './redirect';
import { isNavMenuRoute } from '../utils/navBar';
import { setup } from 'goober';

// Should be called here, and just once
setup(h);

const RollBarComponent = lazy(() =>
  import(/* webpackChunkName: "rollbar-component" */ './rollbarComponent'),
);

export default class App extends Component {
  /** Gets fired when the route changes.
   *	@param {Object} event		"change" event from [preact-router](http://git.io/preact-router)
   *	@param {string} event.url	The newly routed URL
   */
  state = {
    url: document.referrer,
    path: '',
    template: '',
    routeDetails: {},
  };
  constructor() {
    super();
    this.showAppIncompatiableUI =
      !Utils.isClientSideStorageEnabled() || !Utils.isSessionStorageEnabled();
  }
  componentDidMount() {
    //disabling for the new bic flow
    this.identifyUser();

    if (!this.showAppIncompatiableUI) {
      validateAndSetClientIdCookie();
      window.addEventListener('storage', this.validateClientIdStorage);
    }
  }

  isPathExistsInCommonRoutes = (currentPath) => {
    return commonRoutes.some((element) => {
      return element.path === currentPath;
    });
  };

  identifyUser = () => {
    let apiCallFlag = false;
    const currentPath = window.location.pathname;

    if (currentPath === '/') {
      apiCallFlag = true;
    } else {
      let routesListLength = instacredHomeRoutes.length;
      let routesList = instacredHomeRoutes;
      if (this.isPathExistsInCommonRoutes(currentPath)) {
        routesListLength = commonRoutes.length;
        routesList = commonRoutes;
      }
      for (let i = 0; i < routesListLength; i++) {
        const path = routesList[i].path;
        if (path === '/') {
          continue;
        }
        const isIdentifyUserLogic = routesList[i].isIdentifyUserRoute;
        if (currentPath.includes(path) && isIdentifyUserLogic) {
          apiCallFlag = true;
          break;
        }
      }
    }
    if (apiCallFlag) {
      store.dispatch(identifyUserAction());
    }
  };

  handleRoute = (e) => {
    if (Utils.isClientSideStorageEnabled() && Utils.isSessionStorageEnabled()) {
      setSessionStorage('referralUrl', this.state.url);
      const { current, url } = e || {};
      const { props: urlProps } = current || {};
      const { path } = urlProps || {};
      scrollTo();
      this.setState({
        url,
        path,
      });
    }
  };

  validateClientIdStorage(storageEvent) {
    if (storageEvent.key === 'clientId' && !storageEvent.newValue) {
      setLocalStorage(
        CLIENT_ID_COOKIE_KEY,
        store.getState().config.clientId ||
          Utils.getCookie(CLIENT_ID_COOKIE_KEY),
      );
    }
  }

  updateTemplate = (routeDetails) => {
    const { template } = routeDetails || {};
    store.dispatch({
      type: commonActionType.CHANGE_ROUTE,
      payload: { routeDetails },
    });

    store.dispatch({
      type: navBarConfigActionType.RESET,
      payload: {
        isNavMenuConfig: isNavMenuRoute() ? true : false,
      },
    });
    this.setState({ template });
  };

  render() {
    return (
      <Provider store={store}>
        {process.env.NODE_ENV === 'development' && (
          <Helmet
            link={[
              {
                rel: 'preconnect',
                href: 'https://fonts.googleapis.com',
              },
              {
                rel: 'preconnect',
                href: 'https://fonts.gstatic.com',
                crossOriginIsolated: true,
              },
              {
                rel: 'stylesheet',
                href:
                  'https://fonts.googleapis.com/css2?family=Roboto:wght@100;400;500;700;900&display=swap',
              },
              {
                rel: 'stylesheet',
                href:
                  'https://fonts.googleapis.com/css2?family=Poppins:wght@100;200;400;500;600;700;900&display=swap',
              },
            ]}
          />
        )}
        <AppContainer
          currentPath={this.state.path}
          template={this.state.template}>
          <Router onChange={this.handleRoute} default={pageNotFoundRoute.path}>
            {[
              ...commonRoutes,
              ...instacredHomeRoutes,
              ...activationRoutes,
              ...paymentRoutes,
              ...checkoutWidgetRoutes,
            ].map((childRoute) => {
              if (childRoute.authenticate) {
                return (
                  <PrivateRoute
                    path={childRoute.path}
                    component={childRoute.component}
                    updateTemplate={this.updateTemplate}
                  />
                );
              }

              return (
                <AsyncRoute
                  path={childRoute.path}
                  getComponent={() =>
                    import(`../routes/${childRoute.component.toString()}`).then(
                      (module) => {
                        this.updateTemplate(childRoute);
                        return module.default;
                      },
                    )
                  }
                  loading={() => (
                    <div className='loaderContainer'>
                      <Spinner />
                    </div>
                  )}
                />
              );
            })}
            <Redirect
              path='/'
              replace={true}
              to={`${buyWithInstacredLandingRoute.path}${
                window.location.search
              }`}
            />
            <NotFound default />
          </Router>
          <Suspense fallback={null}>
            <RollBarComponent />
          </Suspense>
        </AppContainer>
      </Provider>
    );
  }
}



// WEBPACK FOOTER //
// ./components/app.js